import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { vendorsAPI } from '../services/api';
import Sidebar from '../components/Sidebar';
import './Vendors.css';

const Vendors = () => {
  const navigate = useNavigate();
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, active: 0, inactive: 0 });
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    page: 1,
    limit: 10
  });
  const [pagination, setPagination] = useState({
    page: 1, limit: 10, total: 0, totalPages: 0
  });
  const [filterDropdownOpen, setFilterDropdownOpen] = useState(false);
  const filterDropdownRef = useRef(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [exportModal, setExportModal] = useState({ isOpen: false, config: null, filename: '' });
  const [configCopied, setConfigCopied] = useState(false);

  const fetchVendors = useCallback(async () => {
    try {
      setLoading(true);
      const response = await vendorsAPI.getAll(filters);
      if (response.data.success) {
        setVendors(response.data.data.vendors);
        setPagination(response.data.data.pagination);
      }
    } catch (error) {
      console.error('Failed to fetch vendors:', error);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const fetchStats = async () => {
    try {
      const response = await vendorsAPI.getStats();
      if (response.data.success) {
        setStats(response.data.data.stats);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  useEffect(() => { fetchVendors(); }, [fetchVendors]);
  useEffect(() => { fetchStats(); }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterDropdownRef.current && !filterDropdownRef.current.contains(event.target)) {
        setFilterDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearch = (e) => {
    setFilters(prev => ({ ...prev, search: e.target.value, page: 1 }));
  };

  const handleStatusFilter = (status) => {
    setFilters(prev => ({ ...prev, status: prev.status === status ? '' : status, page: 1 }));
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setFilters(prev => ({ ...prev, page: newPage }));
    }
  };

  const handleDelete = async (vendor) => {
    try {
      await vendorsAPI.delete(vendor.id);
      setDeleteConfirm(null);
      fetchVendors();
      fetchStats();
    } catch (error) {
      console.error('Failed to delete vendor:', error);
    }
  };

  const handleExportConfig = async (vendorId) => {
    try {
      const response = await vendorsAPI.exportConfig(vendorId);
      if (response.data.success) {
        setExportModal({
          isOpen: true,
          config: response.data.data.config,
          filename: response.data.data.filename
        });
        setConfigCopied(false);
      }
    } catch (error) {
      console.error('Failed to export config:', error);
    }
  };

  const handleCopyConfig = () => {
    navigator.clipboard.writeText(JSON.stringify(exportModal.config, null, 2));
    setConfigCopied(true);
    setTimeout(() => setConfigCopied(false), 2000);
  };

  const handleDownloadConfig = () => {
    const blob = new Blob([JSON.stringify(exportModal.config, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = exportModal.filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const filterOptions = [
    { value: '', label: 'All Status' },
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' }
  ];

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric'
    });
  };

  return (
    <div className="dashboard-layout">
      <Sidebar />
      <main className="dashboard-main">
        <div className="dashboard-header">
          <div className="header-left">
            <h1 className="page-title">VENDORS</h1>
            <p className="page-subtitle">WHITELABEL PARTNER MANAGEMENT</p>
          </div>
          <div className="header-right">
            <div className="search-box">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"></circle>
                <path d="m21 21-4.35-4.35"></path>
              </svg>
              <input
                type="text"
                placeholder="Search vendors..."
                value={filters.search}
                onChange={handleSearch}
              />
            </div>
            <div className="custom-dropdown" ref={filterDropdownRef}>
              <button
                className="custom-dropdown-trigger"
                onClick={() => setFilterDropdownOpen(!filterDropdownOpen)}
              >
                <span>{filterOptions.find(opt => opt.value === filters.status)?.label || 'All Status'}</span>
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              {filterDropdownOpen && (
                <div className="custom-dropdown-menu">
                  {filterOptions.map((option) => (
                    <div
                      key={option.value}
                      className={`custom-dropdown-item ${filters.status === option.value ? 'active' : ''}`}
                      onClick={() => {
                        handleStatusFilter(option.value);
                        setFilterDropdownOpen(false);
                      }}
                    >
                      {option.label}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <button className="btn-primary" onClick={() => navigate('/vendors/new')}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="12" y1="5" x2="12" y2="19"></line>
                <line x1="5" y1="12" x2="19" y2="12"></line>
              </svg>
              ADD VENDOR
            </button>
          </div>
        </div>

        <div className="dashboard-content">
          {/* Stats Cards */}
          <div className="stats-grid stats-grid-3">
            <div
              className={`stat-card stat-card-clickable ${filters.status === '' ? 'stat-card-active' : ''}`}
              onClick={() => handleStatusFilter('')}
            >
              <div className="stat-info">
                <span className="stat-label">TOTAL VENDORS</span>
                <span className="stat-value">{stats.total}</span>
              </div>
            </div>
            <div
              className={`stat-card stat-success stat-card-clickable ${filters.status === 'active' ? 'stat-card-active' : ''}`}
              onClick={() => handleStatusFilter('active')}
            >
              <div className="stat-info">
                <span className="stat-label">ACTIVE</span>
                <span className="stat-value">{stats.active}</span>
              </div>
            </div>
            <div
              className={`stat-card stat-error stat-card-clickable ${filters.status === 'inactive' ? 'stat-card-active' : ''}`}
              onClick={() => handleStatusFilter('inactive')}
            >
              <div className="stat-info">
                <span className="stat-label">INACTIVE</span>
                <span className="stat-value">{stats.inactive}</span>
              </div>
            </div>
          </div>

          {/* Vendors Table */}
          <div className="dashboard-section vendors-table-section">
            {loading ? (
              <div className="loading-state">
                <div className="spinner"></div>
                <p>Loading vendors...</p>
              </div>
            ) : vendors.length === 0 ? (
              <div className="empty-state">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M20 7h-9"></path>
                  <path d="M14 17H5"></path>
                  <circle cx="17" cy="17" r="3"></circle>
                  <circle cx="7" cy="7" r="3"></circle>
                </svg>
                <p>No vendors found</p>
              </div>
            ) : (
              <>
                <div className="table-container">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>VENDOR & CODE</th>
                        <th>LINKED AGENCY</th>
                        <th>DOMAIN</th>
                        <th>CONTACT</th>
                        <th>STATUS</th>
                        <th>CREATED</th>
                        <th>ACTIONS</th>
                      </tr>
                    </thead>
                    <tbody>
                      {vendors.map((vendor) => (
                        <tr key={vendor.id}>
                          <td>
                            <div className="vendor-info">
                              <div className="vendor-avatar">
                                {vendor.vendor_name.charAt(0).toUpperCase()}
                              </div>
                              <div className="vendor-details">
                                <span className="vendor-name">{vendor.vendor_name}</span>
                                <span className="vendor-code">{vendor.vendor_code} / {vendor.vendor_id}</span>
                              </div>
                            </div>
                          </td>
                          <td>
                            <span className="vendor-agency">
                              {vendor.agency_name || <span className="text-muted">Not linked</span>}
                            </span>
                          </td>
                          <td>
                            <span className="vendor-domain">
                              {vendor.custom_domain || <span className="text-muted">-</span>}
                            </span>
                          </td>
                          <td>
                            <span className="vendor-email">
                              {vendor.contact_email || <span className="text-muted">-</span>}
                            </span>
                          </td>
                          <td>
                            <span className={`status-badge ${vendor.is_active ? 'status-approved' : 'status-rejected'}`}>
                              {vendor.is_active ? 'ACTIVE' : 'INACTIVE'}
                            </span>
                          </td>
                          <td>
                            <span className="vendor-date">{formatDate(vendor.created_at)}</span>
                          </td>
                          <td>
                            <div className="actions-cell">
                              <button
                                className="btn-action btn-edit"
                                onClick={() => navigate(`/vendors/${vendor.id}/edit`)}
                                title="Edit"
                              >
                                EDIT
                              </button>
                              <button
                                className="btn-action btn-export"
                                onClick={() => handleExportConfig(vendor.id)}
                                title="Export Config"
                              >
                                EXPORT
                              </button>
                              <button
                                className="btn-action btn-delete"
                                onClick={() => setDeleteConfirm(vendor)}
                                title="Delete"
                              >
                                DELETE
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <div className="table-footer">
                  <div className="pagination-info">
                    Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
                    {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
                    {pagination.total} vendors
                  </div>
                  <div className="pagination-controls">
                    <button
                      className="pagination-btn"
                      disabled={pagination.page === 1}
                      onClick={() => handlePageChange(pagination.page - 1)}
                    >
                      Previous
                    </button>
                    {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                      let pageNum;
                      if (pagination.totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (pagination.page <= 3) {
                        pageNum = i + 1;
                      } else if (pagination.page >= pagination.totalPages - 2) {
                        pageNum = pagination.totalPages - 4 + i;
                      } else {
                        pageNum = pagination.page - 2 + i;
                      }
                      return (
                        <button
                          key={pageNum}
                          className={`pagination-btn ${pagination.page === pageNum ? 'active' : ''}`}
                          onClick={() => handlePageChange(pageNum)}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                    <button
                      className="pagination-btn"
                      disabled={pagination.page === pagination.totalPages}
                      onClick={() => handlePageChange(pagination.page + 1)}
                    >
                      Next
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </main>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-icon danger">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 6h18"></path>
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                </svg>
              </div>
              <h2 className="modal-title">Delete Vendor</h2>
              <p className="modal-subtitle">Are you sure you want to delete "{deleteConfirm.vendor_name}"?</p>
            </div>
            <div className="modal-footer">
              <button className="btn-modal-close" onClick={() => setDeleteConfirm(null)}>Cancel</button>
              <button className="btn-modal-danger" onClick={() => handleDelete(deleteConfirm)}>Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* Export Config Modal */}
      {exportModal.isOpen && (
        <div className="modal-overlay" onClick={() => setExportModal({ isOpen: false, config: null, filename: '' })}>
          <div className="modal-content export-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Export Vendor Config</h2>
              <p className="modal-subtitle">{exportModal.filename}</p>
            </div>
            <div className="modal-body">
              <pre className="config-preview">{JSON.stringify(exportModal.config, null, 2)}</pre>
            </div>
            <div className="modal-footer export-modal-footer">
              <button className="btn-modal-close" onClick={() => setExportModal({ isOpen: false, config: null, filename: '' })}>
                Close
              </button>
              <button className={`btn-primary ${configCopied ? 'btn-copied' : ''}`} onClick={handleCopyConfig}>
                {configCopied ? 'Copied!' : 'Copy JSON'}
              </button>
              <button className="btn-primary" onClick={handleDownloadConfig}>
                Download
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Vendors;
